import { fetchApi, parseError } from '@/api/http'
import { markPlatformSessionActive } from '@/utils/platformAdminAuth'
import { forceRelogin } from '@/utils/authFlow'

async function platformFetch(path: string, options?: RequestInit): Promise<Response> {
  return fetchApi(path, options)
}

async function handlePlatformJson<T>(res: Response): Promise<T> {
  const text = await res.text()
  if (res.status === 401) {
    let msg = 'Sessão da plataforma inválida ou expirada. Faça login novamente.'
    try {
      const d = JSON.parse(text) as { error?: string; detail?: string }
      msg = d?.error ?? d?.detail ?? msg
    } catch {
      /* ignore */
    }
    forceRelogin('platform', msg)
  }
  if (!res.ok) return parseError(res, text)
  if (res.status === 204 || !text) return undefined as T
  return JSON.parse(text) as T
}

export interface PlatformTenantRow {
  id: number
  slug: string
  nome: string
  ativo: number
  plano: string | null
  limite_usuarios: number | null
  limite_agendamentos_mes: number | null
  adesao_em?: string | null
  proxima_cobranca?: string | null
  criado_em: string
  atualizado_em: string
  usuarios_total: number
  admins_ativos: number
  profissionais_total: number
  unidades_total: number
  agendamentos_total: number
  agendamentos_mes_atual: number
  ultimo_agendamento_em: string | null
  dias_sem_actividade: number
  quota_usuarios_excedida?: boolean
  quota_agendamentos_mes_excedida?: boolean
}

export interface PlatformTenantAdminRow {
  id: number
  email: string
  nome: string | null
  ativo: number
  criado_em?: string
}

export type PlatformTenantStoreAppStatus = 'rascunho' | 'pronto' | 'enviado' | 'live'

export type PlatformTenantStoreBuildStatus = 'idle' | 'queued' | 'building' | 'ready' | 'failed'

export interface PlatformTenantStoreAppBuild {
  status: PlatformTenantStoreBuildStatus
  run_id: string | null
  run_url: string | null
  aab_ready: boolean
  ipa_ready: boolean
  error: string | null
  started_at: string | null
  finished_at: string | null
}

export interface PlatformTenantStoreApp {
  app_name: string | null
  app_short_name: string | null
  android_app_id: string | null
  ios_bundle_id: string | null
  icon_url: string | null
  listing_description: string | null
  status: PlatformTenantStoreAppStatus
  play_url: string | null
  appstore_url: string | null
  build: PlatformTenantStoreAppBuild
}

export interface PlatformTenantDetail extends PlatformTenantRow {
  admins: PlatformTenantAdminRow[]
  store_app: PlatformTenantStoreApp
}

export type PlatformTenantSort =
  | 'id'
  | 'nome'
  | 'criado_em'
  | 'ultimo_agendamento'
  | 'inactividade'

export interface PlatformTenantListParams {
  q?: string
  ativo?: 'all' | '1' | '0'
  page?: number
  limit?: number
  sort?: PlatformTenantSort
}

export interface PlatformTenantListResponse {
  items: PlatformTenantRow[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface PlatformAuditEntry {
  id: number
  tenant_id: number | null
  action: string
  actor: string
  detail: Record<string, unknown> | null
  criado_em: string
}

function buildTenantsQuery(params?: PlatformTenantListParams): string {
  const sp = new URLSearchParams()
  if (params?.q?.trim()) sp.set('q', params.q.trim())
  if (params?.ativo && params.ativo !== 'all') sp.set('ativo', params.ativo)
  if (params?.page) sp.set('page', String(params.page))
  if (params?.limit) sp.set('limit', String(params.limit))
  if (params?.sort) sp.set('sort', params.sort)
  const qs = sp.toString()
  return qs ? `?${qs}` : ''
}

export async function listPlatformTenants(
  params?: PlatformTenantListParams
): Promise<PlatformTenantListResponse> {
  const res = await platformFetch(`/api/platform/tenants${buildTenantsQuery(params)}`)
  return handlePlatformJson<PlatformTenantListResponse>(res)
}

export async function getPlatformTenant(id: number): Promise<PlatformTenantDetail> {
  const res = await platformFetch(`/api/platform/tenants/${id}`)
  return handlePlatformJson<PlatformTenantDetail>(res)
}

export async function checkPlatformTenantSlug(
  slug: string
): Promise<{ slug: string; available: boolean; reason?: string }> {
  const res = await platformFetch(
    `/api/platform/tenants/check-slug?slug=${encodeURIComponent(slug.trim())}`
  )
  return handlePlatformJson(res)
}

export async function getPlatformTenantAudit(
  tenantId: number,
  limit = 50
): Promise<{ items: PlatformAuditEntry[] }> {
  const res = await platformFetch(`/api/platform/tenants/${tenantId}/audit?limit=${limit}`)
  return handlePlatformJson(res)
}

/** Login: valida operador + PLATFORM_ADMIN_TOKEN e recebe cookie de sessão (4h). */
export async function loginPlatform(masterToken: string, operatorUser = ''): Promise<void> {
  const res = await fetchApi('/api/platform/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user: operatorUser.trim(),
      token: masterToken.trim(),
    }),
  })
  await handlePlatformJson<{ ok: boolean }>(res)
  markPlatformSessionActive()
}

export async function logoutPlatform(): Promise<void> {
  try {
    await fetchApi('/api/platform/logout', { method: 'POST' })
  } catch {
    /* ignore */
  }
}

export async function verifyPlatformSession(): Promise<void> {
  const res = await fetchApi('/api/platform/ping')
  await handlePlatformJson<{ ok: boolean }>(res)
  markPlatformSessionActive()
}

export async function patchPlatformTenant(
  id: number,
  body: Partial<{
    ativo: boolean
    slug: string
    nome: string
    plano: string | null
    limite_usuarios: number | null
    limite_agendamentos_mes: number | null
  }>
): Promise<PlatformTenantRow> {
  const res = await platformFetch(`/api/platform/tenants/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handlePlatformJson<PlatformTenantRow>(res)
}

export async function patchPlatformTenantStoreApp(
  id: number,
  body: Partial<{
    app_name: string | null
    app_short_name: string | null
    android_app_id: string | null
    ios_bundle_id: string | null
    icon_url: string | null
    listing_description: string | null
    status: PlatformTenantStoreAppStatus
    play_url: string | null
    appstore_url: string | null
  }>
): Promise<PlatformTenantDetail> {
  const res = await platformFetch(`/api/platform/tenants/${id}/store-app`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handlePlatformJson<PlatformTenantDetail>(res)
}

export async function resetPlatformTenantStoreBuild(
  id: number
): Promise<{ ok: boolean; tenant: PlatformTenantDetail }> {
  const res = await platformFetch(`/api/platform/tenants/${id}/store-app/build/reset`, {
    method: 'POST',
  })
  return handlePlatformJson(res)
}

export async function triggerPlatformTenantStoreBuild(
  id: number
): Promise<{
  ok: boolean
  build_ref: string
  run_url: string
  tenant: PlatformTenantDetail
}> {
  const res = await platformFetch(`/api/platform/tenants/${id}/store-app/build`, {
    method: 'POST',
  })
  return handlePlatformJson(res)
}

export async function downloadPlatformTenantStoreBuild(
  tenantId: number,
  kind: 'aab' | 'ipa'
): Promise<void> {
  const res = await platformFetch(`/api/platform/tenants/${tenantId}/store-app/build/download/${kind}`)
  if (!res.ok) {
    const text = await res.text()
    return parseError(res, text)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = kind === 'ipa' ? 'app-release.ipa' : 'app-release.aab'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function createPlatformTenant(body: {
  slug: string
  nome?: string
  adminEmail: string
  adminSenha: string
  adminNome?: string
}): Promise<{ tenantId: number; slug: string; nome: string; adminEmail: string }> {
  const res = await platformFetch('/api/platform/tenants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      slug: body.slug,
      nome: body.nome,
      adminEmail: body.adminEmail,
      adminSenha: body.adminSenha,
      adminNome: body.adminNome,
    }),
  })
  return handlePlatformJson(res)
}

export async function createPlatformTenantAdmin(
  tenantId: number,
  body: { email: string; senha: string; nome?: string }
): Promise<PlatformTenantAdminRow> {
  const res = await platformFetch(`/api/platform/tenants/${tenantId}/admins`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handlePlatformJson(res)
}

export async function patchPlatformTenantAdmin(
  tenantId: number,
  adminId: number,
  body: Partial<{ nome: string; senha: string; ativo: boolean }>
): Promise<PlatformTenantAdminRow> {
  const res = await platformFetch(`/api/platform/tenants/${tenantId}/admins/${adminId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handlePlatformJson(res)
}

export async function impersonatePlatformTenantAdmin(
  tenantId: number,
  adminId?: number
): Promise<{
  email: string
  adminNome: string | null
  tenantSlug: string
  tenantNome: string
}> {
  const res = await platformFetch(`/api/platform/tenants/${tenantId}/impersonate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(adminId != null ? { adminId } : {}),
  })
  return handlePlatformJson(res)
}

export async function deletePlatformTenant(
  id: number,
  confirmSlug: string
): Promise<{ ok: boolean; deletedId: number; slug: string }> {
  const res = await platformFetch(`/api/platform/tenants/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmSlug }),
  })
  return handlePlatformJson(res)
}

export interface PlatformMonthlyPoint {
  month: string
  count: number
}

export interface PlatformStatsTotals {
  tenants_total: number
  tenants_ativos: number
  tenants_suspensos: number
  usuarios_total: number
  profissionais_total: number
  unidades_total: number
  admins_ativos: number
  agendamentos_total: number
}

export interface PlatformStats {
  tenantsByMonth: PlatformMonthlyPoint[]
  agendamentosByMonth: PlatformMonthlyPoint[]
  totals: PlatformStatsTotals
  agendamentosUltimos30d: number
  agendamentosPrev30d: number
  agendamentosNaoCancelados: number
  feedbacksTotal: number | null
  comprasTotal: number | null
}

export async function getPlatformStats(): Promise<PlatformStats> {
  const res = await platformFetch('/api/platform/stats')
  return handlePlatformJson<PlatformStats>(res)
}
